import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  BREAKPOINTS_EM,
  Breadcrumb,
  COLORS,
  cssByBreakpoint,
  Div,
  SPACINGS,
} from '@figure/figure-frontlib-gui';
import { Header } from './Header';

const Wrapper = styled.main`
  ${({ customMaxWidth }) => customMaxWidth && `max-width: ${customMaxWidth};`}}
  margin-left: auto;
  margin-right: auto;
  // Overrides 12px padding on main
  && {
    padding: 0px;
  }

`;

const Content = styled.div`
  ${({ hasSideContent }) =>
    hasSideContent &&
    cssByBreakpoint(
      [
        css`
          display: flex;
          flex-direction: column;
        `,
        css`
          flex-direction: row;
          gap: ${SPACINGS.XS}px;

          > :nth-child(2) {
            max-width: 60%;
          }

          > :last-child {
            max-width: 40%;
          }
        `,
      ],
      BREAKPOINTS_EM.MD
    )}
`;

export const SideContent = ({ children, ...rest }) => <div {...rest}>{children}</div>;
SideContent.propTypes = {
  children: PropTypes.node.isRequired,
};

const FooterWrapper = styled.div`
  margin-top: ${SPACINGS.X2L}px;
`;
export const Footer = ({ children, ...rest }) => (
  <FooterWrapper {...rest}>{children}</FooterWrapper>
);
Footer.propTypes = {
  children: PropTypes.node.isRequired,
};

export const Card = ({
  className,
  pageTitle = '',
  headerCopy = '',
  customMaxWidth = '84rem',
  children,
  breadcrumbs,
  useNoPadding,
  progress,
}) => {
  const childrenArray = React.Children.toArray(children);
  const childrenCopy = childrenArray.filter(
    (child) => React.isValidElement(child) && child.type !== SideContent && child.type !== Footer
  );
  const sideContent = childrenArray.filter(
    (child) => React.isValidElement(child) && child.type === SideContent
  );
  const footer = childrenArray.filter(
    (child) => React.isValidElement(child) && child.type === Footer
  );

  const hasSideContent = sideContent.length > 0;
  const hasFooter = footer.length > 0;

  return (
    <Wrapper className={className} customMaxWidth={customMaxWidth}>
      <Content hasSideContent={hasSideContent}>
        {breadcrumbs && <Breadcrumb levels={breadcrumbs} />}
        <Div
          $css={css`
            padding: ${useNoPadding ? '0' : SPACINGS.X2L}px;
            background-color: ${COLORS.WHITE};
          `}
        >
          {(pageTitle || headerCopy) && (
            <Header pageTitle={pageTitle} headerCopy={headerCopy} progress={progress} />
          )}
          {childrenCopy}
        </Div>

        {hasSideContent && (
          <Div
            $css={css`
              padding: ${SPACINGS.X2L}px;
              background-color: ${COLORS.WHITE};
            `}
          >
            {sideContent}
          </Div>
        )}
      </Content>
      {hasFooter && <Footer>{footer}</Footer>}
    </Wrapper>
  );
};
Card.propTypes = {
  className: PropTypes.string,
  pageTitle: PropTypes.string,
  headerCopy: PropTypes.string,
  customMaxWidth: PropTypes.string,
  children: PropTypes.node.isRequired,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      page: PropTypes.string.isRequired,
      to: PropTypes.string,
      href: PropTypes.string,
    })
  ),
  useNoPadding: PropTypes.bool,
  progress: PropTypes.shape({
    currentStep: PropTypes.number,
    totalSteps: PropTypes.number,
  }),
};
Card.defaultProps = {
  className: '',
  pageTitle: '',
  headerCopy: '',
  customMaxWidth: '',
  breadcrumbs: null,
  useNoPadding: false,
  progress: null,
};

Card.SideContent = SideContent;
Card.Footer = Footer;
